/**
 * How a RUNTIME consumer reads what another process sent it (D35's last
 * point, PLAN §6.8): **tolerant** — unknown fields are stripped, not refused.
 *
 * Why. Every wire shape here is `.strict()`, which is right for what a server
 * ACCEPTS (a request, an inbound frame) and for what a server SENDS (the
 * router checks its own answers). It is wrong for what a client RECEIVES from
 * a newer peer: an additive change — a new optional field in a response —
 * made every older client (an SPA built before it, a service-worker-cached
 * SPA, a pinned production process, a node on last week's release) refuse
 * the answer as "contract drift". That happened: a deploy that added a field
 * broke every open SPA until it reloaded.
 *
 * So a runtime reader parses with `'tolerant'`, and tests (and the
 * `test:api` contract checks, which run the server side) keep `'strict'`, so
 * real drift — a field one side forgot to declare — still fails in CI. The
 * mode is an explicit argument everywhere (CLAUDE.md: explicit dependencies):
 * the host that wires a client decides, never a global.
 *
 * What tolerant does NOT forgive: a missing field, a wrong type, an unknown
 * enum value. Those are breaking changes, which the contract forbids while an
 * older peer may still be listening (§6.8: additive only).
 *
 * How. The strict schema is tried first (the common case: nothing unknown).
 * On failure, if EVERY issue is an unknown key — directly, or inside a union
 * branch whose only issues are unknown keys — those keys are removed from a
 * copy of the input and the parse is retried; anything else is returned as
 * the strict failure. Stripping on the value, rather than rebuilding every
 * schema without `.strict()`, keeps one schema per shape and works through
 * unions, records, refinements and transforms alike.
 */
import type { z } from 'zod';

/**
 * - `strict`: exactly the schema (tests, and what a server accepts from a user).
 * - `tolerant`: unknown keys dropped (a runtime reader, D35).
 * - `preserving`: validated like `tolerant`, but the unknown keys are KEPT on the result, at the paths they came
 *   from (D37, docs/compatibility.md §4.5 — protobuf's unknown fields). For a reader that may write the record back:
 *   the node's copies of the cloud's records, the peer link. The type does not show them; a spread carries them.
 */
export type ContractParsing = 'strict' | 'tolerant' | 'preserving';

/** Unknown-key removals are retried at most this often: each pass can reveal a union branch's own unknown keys. */
const MAX_STRIP_PASSES = 8;

type PathKey = PropertyKey;
type Strip = { readonly path: readonly PathKey[]; readonly keys: readonly string[] };

/** The issues' unknown keys to strip, or null when some issue is not an unknown key (real drift). */
function stripsFor(issues: readonly z.core.$ZodIssue[], prefix: readonly PathKey[]): Strip[] | null {
  const out: Strip[] = [];
  for (const issue of issues) {
    const strips = stripsForIssue(issue, [...prefix, ...issue.path]);
    if (strips === null) return null;
    out.push(...strips);
  }
  return out;
}

/** One issue's removals: its own unknown keys, the cheapest union branch's, or null for anything else. */
function stripsForIssue(issue: z.core.$ZodIssue, path: readonly PathKey[]): Strip[] | null {
  if (issue.code === 'unrecognized_keys') return [{ path, keys: issue.keys }];
  if (issue.code !== 'invalid_union') return null;
  // The branch needing the fewest removals; a branch with any other issue is not a candidate.
  let best: Strip[] | null = null;
  for (const branch of issue.errors) {
    const strips = stripsFor(branch, path);
    if (strips !== null && (best === null || count(strips) < count(best))) best = strips;
  }
  return best;
}

function count(strips: readonly Strip[]): number {
  return strips.reduce((n, s) => n + s.keys.length, 0);
}

function isRecord(value: unknown): value is Record<PropertyKey, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A copy of `value` with `keys` removed from the object at `path`; untouched branches are shared, never mutated. */
function without(value: unknown, path: readonly PathKey[], keys: readonly string[]): unknown {
  const [head, ...rest] = path;
  if (head === undefined) {
    if (!isRecord(value)) return value;
    const copy: Record<PropertyKey, unknown> = { ...value };
    for (const key of keys) delete copy[key];
    return copy;
  }
  if (Array.isArray(value) && typeof head === 'number') {
    const copy = [...value];
    copy[head] = without(copy[head], rest, keys);
    return copy;
  }
  if (isRecord(value)) return { ...value, [head]: without(value[head], rest, keys) };
  return value;
}

/** The value at `path` in `value`, or undefined. */
function at(value: unknown, path: readonly PathKey[]): unknown {
  let current = value;
  for (const key of path) {
    if (Array.isArray(current) && typeof key === 'number') current = current[key];
    else if (isRecord(current)) current = current[key];
    else return undefined;
  }
  return current;
}

/**
 * Put the unknown keys `strips` removed from `original` back onto `data`, where they were (`preserving`). In place:
 * `data` is the value zod just built — every object at a strip's path is a fresh object of a `z.object` (only an
 * object schema reports unknown keys), never one shared with the input — and a key the schema set wins.
 */
function restore(data: unknown, original: unknown, strips: readonly Strip[]): void {
  for (const strip of strips) {
    const source = at(original, strip.path);
    const target = at(data, strip.path);
    if (!isRecord(source) || !isRecord(target)) continue;
    for (const key of strip.keys) if (Object.hasOwn(source, key) && !Object.hasOwn(target, key)) target[key] = source[key];
  }
}

/**
 * `schema.safeParse(value)` in the given mode. Strict: exactly that. Tolerant:
 * unknown keys anywhere are dropped first (see the module comment); the
 * result's data never carries them. Preserving: validated as tolerant, and the
 * dropped keys are put back on the result (`ContractParsing`).
 */
export function parseContract<S extends z.ZodType>(schema: S, value: unknown, parsing: ContractParsing): z.ZodSafeParseResult<z.output<S>> {
  const first = schema.safeParse(value);
  if (first.success || parsing === 'strict') return first;
  let current = value;
  let result: z.ZodSafeParseResult<z.output<S>> = first;
  const applied: Strip[] = [];
  for (let pass = 0; pass < MAX_STRIP_PASSES && !result.success; pass += 1) {
    const strips = stripsFor(result.error.issues, []);
    if (strips === null || strips.length === 0) return first;
    for (const strip of strips) current = without(current, strip.path, strip.keys);
    applied.push(...strips);
    result = schema.safeParse(current);
  }
  if (!result.success) return first;
  // Foreign keys re-attached: zod's type still holds for every key it names, and the extra ones are invisible to it
  // by design (a spread carries them to the write-back).
  if (parsing === 'preserving') restore(result.data, value, applied);
  return result;
}

/** The unknown keys a tolerant parse would drop, as dotted paths (for a warning or a test); empty when none. */
export function unknownKeys(schema: z.ZodType, value: unknown): string[] {
  const result = schema.safeParse(value);
  if (result.success) return [];
  const strips = stripsFor(result.error.issues, []) ?? [];
  return strips.flatMap((s) => s.keys.map((k) => [...s.path.map(String), k].join('.')));
}

/** One element a per-element parse skipped: where it was, and why (zod's first issue, for a log line). */
export type ElementRejection = { readonly index: number; readonly reason: string };

/**
 * Parse a collection ONE ELEMENT AT A TIME (D37, docs/compatibility.md §4.3):
 * an element that fails — a row of a kind this build does not know, a record
 * with a value it cannot read — is skipped and reported, never its whole page.
 * Before this, one bad row dropped a 1000-row IPC page and the pinned
 * production ran silently with rows missing. The caller logs the rejections.
 */
export function parseElements<S extends z.ZodType>(
  schema: S,
  values: readonly unknown[],
  parsing: ContractParsing,
): { readonly items: z.output<S>[]; readonly rejected: ElementRejection[] } {
  const items: z.output<S>[] = [];
  const rejected: ElementRejection[] = [];
  values.forEach((value, index) => {
    const result = parseContract(schema, value, parsing);
    if (result.success) items.push(result.data);
    else rejected.push({ index, reason: describeIssue(result.error.issues[0]) });
  });
  return { items, rejected };
}

function describeIssue(issue: z.core.$ZodIssue | undefined): string {
  if (issue === undefined) return 'invalid';
  const path = issue.path.map(String).join('.');
  return path === '' ? issue.message : `${path}: ${issue.message}`;
}

/**
 * Read a STORED document (D37, docs/compatibility.md §3 finding 5): tolerantly, because after a rollback an older
 * Lambda or state service reads what a newer one wrote, and one field it added must not make every read a 500.
 * `'preserving'` for a record the reader may write back whole (the node's copies of the cloud's records): what it
 * does not know stays on the record, and a spread writes it back. The cloud reads `'tolerant'`: its router refuses
 * a response carrying a field the contract does not name (a stray key must never reach a client).
 * Throws — like the `schema.parse` it replaces — when the document is not readable at all (a missing field, a wrong
 * type): that is data this release cannot use, and the store's caller already treats a throw as a failed read.
 */
export function parseStored<S extends z.ZodType>(schema: S, value: unknown, parsing: 'tolerant' | 'preserving'): z.output<S> {
  const result = parseContract(schema, value, parsing);
  if (result.success) return result.data;
  throw result.error;
}
